/**
 * Animated stage background for Card Match. Pure CSS — conic sweep, soft
 * radial glows, sparse star field. Mounted as the FIRST child of the stage
 * so it renders behind everything else.
 *
 * Adapted from the Anagram / WhatIs pattern, retuned for the Card Match
 * brand palette (purple + cyan + magenta).
 */

const ID = 'cm-animated-bg';

export interface AnimatedBgHandle {
  el: HTMLDivElement;
  setVisible: (v: boolean) => void;
  dispose: () => void;
}

export function createAnimatedBg(mount: HTMLElement): AnimatedBgHandle {
  if (!document.getElementById(`${ID}-style`)) {
    const style = document.createElement('style');
    style.id = `${ID}-style`;
    style.textContent = `
      @keyframes cm-bg-sweep {
        0%   { transform: translate(-10%, -10%) rotate(0deg); }
        50%  { transform: translate(10%, 10%)  rotate(180deg); }
        100% { transform: translate(-10%, -10%) rotate(360deg); }
      }
      @keyframes cm-bg-pulse {
        0%, 100% { opacity: 0.55; }
        50%      { opacity: 0.90; }
      }
      @keyframes cm-bg-stars-drift {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-8px); }
      }
      .cm-bg-root {
        position: absolute;
        inset: 0;
        overflow: hidden;
        z-index: 0;
        pointer-events: none;
        background:
          radial-gradient(ellipse at 50% 30%, rgba(31, 27, 75, 0.85) 0%, rgba(10, 14, 39, 0.98) 60%, #05071a 100%);
      }
      .cm-bg-sweep {
        position: absolute;
        width: 200%;
        height: 200%;
        left: -50%;
        top: -50%;
        background:
          conic-gradient(from 0deg,
            rgba(168, 85, 247, 0.0)  0%,
            rgba(168, 85, 247, 0.14) 18%,
            rgba(6, 182, 212, 0.0)   35%,
            rgba(6, 182, 212, 0.10)  55%,
            rgba(236, 72, 153, 0.0)  72%,
            rgba(236, 72, 153, 0.10) 86%,
            rgba(168, 85, 247, 0.0)  100%);
        animation: cm-bg-sweep 38s linear infinite;
        opacity: 0.7;
        mix-blend-mode: screen;
      }
      .cm-bg-glow {
        position: absolute;
        inset: 0;
        background:
          radial-gradient(circle at 28% 72%, rgba(168, 85, 247, 0.22) 0%, transparent 42%),
          radial-gradient(circle at 72% 28%, rgba(6, 182, 212, 0.20) 0%, transparent 42%),
          radial-gradient(circle at 50% 90%, rgba(236, 72, 153, 0.16) 0%, transparent 38%);
        animation: cm-bg-pulse 7s ease-in-out infinite;
        mix-blend-mode: screen;
      }
      .cm-bg-stars {
        position: absolute;
        inset: 0;
        background-image:
          radial-gradient(2px 2px at 20% 30%, rgba(255,255,255,0.55), transparent),
          radial-gradient(1px 1px at 80% 70%, rgba(168,85,247,0.55), transparent),
          radial-gradient(2px 2px at 60% 20%, rgba(6,182,212,0.6), transparent),
          radial-gradient(1px 1px at 35% 80%, rgba(236,72,153,0.55), transparent),
          radial-gradient(1px 1px at 75% 50%, rgba(255,255,255,0.4), transparent),
          radial-gradient(1px 1px at 10% 60%, rgba(255,255,255,0.4), transparent),
          radial-gradient(2px 2px at 90% 15%, rgba(6,182,212,0.55), transparent),
          radial-gradient(1px 1px at 45% 12%, rgba(255,255,255,0.5), transparent),
          radial-gradient(1px 1px at 25% 50%, rgba(236,72,153,0.45), transparent);
        opacity: 0.6;
        animation: cm-bg-stars-drift 14s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }

  const el = document.createElement('div');
  el.id = ID;
  el.className = 'cm-bg-root';
  el.setAttribute('data-overlay', 'animated-bg');
  el.innerHTML = `
    <div class="cm-bg-sweep"></div>
    <div class="cm-bg-glow"></div>
    <div class="cm-bg-stars"></div>
  `;
  mount.insertBefore(el, mount.firstChild);

  return {
    el,
    setVisible(v: boolean) {
      el.style.display = v ? 'block' : 'none';
    },
    dispose() {
      el.remove();
    },
  };
}
